import React from "react";
import { useTranslation } from "react-i18next";
import { Select } from "components/select/index";
import {
  EmailBuilderBoundedRange,
  EmailBuilderColorOverrideField,
} from "../../shell/panel/blockSettings/EmailBuilderBlockSettingsShared";
import { EmailBuilderSettingsPaddingSection } from "../controls/EmailBuilderSettingsPaddingSection";
import { useEmailBuilderSettingsOverlayPortalId } from "../../shell/canvas/settings/emailBuilderSettingsOverlayPortal";
import { SettingSection } from "../controls/SettingSection";
import {
  baselineDividerPadding,
  baselineDividerThickness,
  baselineDividerWidthPct,
} from "../../domain/baselines";
import { isPaddingInactive } from "../settingsSectionActivation";
import {
  isDividerColorInherited,
  resolveDividerColor,
} from "../../domain/text-theme";
import type { DividerBlock } from "../../domain/types";
import type { BlockSettingsProps } from "./types";
import { useEmailBuilderBlockContrastAgainst } from "../useEmailBuilderBlockSurfaceColor";

const { Option } = Select;

const DIVIDER_STYLE_LABELS: Record<DividerBlock["style"], string> = {
  solid: "Solid",
  dashed: "Dashed",
  dotted: "Dotted",
};

export function DividerBlockSettings({
  block,
  patch,
  textTheme,
  rowId,
  columnId,
}: BlockSettingsProps & { block: DividerBlock }) {
  const { t } = useTranslation();
  const overlayPortalId = useEmailBuilderSettingsOverlayPortalId();
  const blockContrastAgainst = useEmailBuilderBlockContrastAgainst(rowId, columnId);

  return (
    <>
      <SettingSection sectionId="layout" title={t("Layout")}>
        <EmailBuilderBoundedRange
          label={t("Width %")}
          value={block.widthPct}
          min={10}
          max={100}
          defaultValue={baselineDividerWidthPct()}
          onChange={widthPct => {
            if (widthPct === undefined) {
              return;
            }
            patch({ widthPct });
          }}
        />
      </SettingSection>

      <SettingSection
        sectionId="padding"
        title={t("Padding")}
        activation={{
          mode: "optional",
          isActive: !isPaddingInactive(block.padding),
          onAdd: () => patch({ padding: { ...baselineDividerPadding() } }),
        }}
      >
        <EmailBuilderSettingsPaddingSection
          fieldLabel={t("Divider padding (px)")}
          value={block.padding}
          defaultValue={baselineDividerPadding()}
          onChange={padding => {
            patch({ padding: { ...padding } });
          }}
        />
      </SettingSection>

      <SettingSection sectionId="appearance" title={t("Appearance")}>
        <EmailBuilderColorOverrideField
          showFieldLabel={false}
          label={t("Color")}
          color={resolveDividerColor(block, textTheme)}
          hasOverride={!isDividerColorInherited(block)}
          contrastAgainst={blockContrastAgainst}
          onChange={color => {
            patch({
              color: color === textTheme.dividerColor ? null : color,
            });
          }}
          onReset={() => patch({ color: null })}
        />
        <EmailBuilderBoundedRange
          label={t("Thickness (px)")}
          value={block.thickness}
          min={1}
          max={12}
          defaultValue={baselineDividerThickness()}
          onChange={thickness => {
            if (thickness === undefined) {
              return;
            }
            patch({ thickness });
          }}
        />
        <Select
          label={t("Style")}
          defaultValue={block.style}
          onChange={(style: DividerBlock["style"]) => patch({ style })}
          usePortal
          portalId={overlayPortalId}
        >
          {(["solid", "dashed", "dotted"] as const).map(style => (
            <Option key={style} value={style}>
              <span>{t(DIVIDER_STYLE_LABELS[style])}</span>
            </Option>
          ))}
        </Select>
      </SettingSection>
    </>
  );
}
